import type { AnchorHTMLAttributes, FC, ReactNode } from "react";
import { Link, type LinkProps } from "react-router";

/**
 * `Button` のリンク版。SPA 内遷移用 (= react-router の `<Link>` ベース) と外部リンク用
 * (= `<a>` ベース) の両方を、`Button` と揃った外観で提供する。
 *
 * これまで address-form / WizardActions / ActiveReturnView 等で同じクラス
 * 文字列 (`inline-flex items-center justify-center rounded ...`) をコピペしていたのを集約。
 *
 * - `to` が string なら `<Link>` (= SPA 内遷移)
 * - `href` が string なら `<a>` (= 外部リンク / mailto / tel など)
 * - 両方未指定はビルドエラー (= props の discriminated union)
 *
 * variant / size は `Button` と同じ意味 (primary / outline / subtle, md / sm)。
 */
export type LinkButtonVariant = "primary" | "outline" | "subtle";
export type LinkButtonSize = "md" | "sm";

const VARIANT_CLASSES: Record<LinkButtonVariant, string> = {
  primary: "bg-neutral-900 text-white hover:bg-neutral-800",
  outline: "border border-neutral-300 text-neutral-900 hover:bg-neutral-50",
  subtle: "text-neutral-700 hover:text-neutral-900 hover:underline underline-offset-4",
};

const SIZE_CLASSES: Record<LinkButtonSize, string> = {
  md: "px-5 py-2.5 text-sm",
  sm: "px-3 py-1.5 text-xs",
};

const baseClass = (variant: LinkButtonVariant, size: LinkButtonSize, extra?: string) =>
  `inline-flex items-center justify-center rounded font-medium transition-colors ${VARIANT_CLASSES[variant]} ${SIZE_CLASSES[size]} ${extra ?? ""}`;

type CommonProps = {
  variant?: LinkButtonVariant;
  size?: LinkButtonSize;
  className?: string;
  children: ReactNode;
};

type RouterLinkProps = CommonProps &
  Omit<LinkProps, "className" | "children"> & {
    to: LinkProps["to"];
    href?: never;
  };

type AnchorProps = CommonProps &
  Omit<AnchorHTMLAttributes<HTMLAnchorElement>, "className" | "children" | "href"> & {
    href: string;
    to?: never;
  };

type Props = RouterLinkProps | AnchorProps;

export const LinkButton: FC<Props> = ({
  variant = "primary",
  size = "md",
  className,
  children,
  ...rest
}) => {
  if ("to" in rest && rest.to !== undefined) {
    const { to, ...linkRest } = rest;
    return (
      <Link to={to} className={baseClass(variant, size, className)} {...linkRest}>
        {children}
      </Link>
    );
  }
  // anchor 経路
  const { href, ...anchorRest } = rest as AnchorProps;
  return (
    <a href={href} className={baseClass(variant, size, className)} {...anchorRest}>
      {children}
    </a>
  );
};
